/**
 * Ленивые страничные компоненты: каждая группа страниц собрана в своё
 * расширение Битрикса (см. ../../../extensions.json) и подгружается по имени
 * компонента в момент, когда App встречает его в struct.
 *
 * resolveComponent(имя, ключ блока) — то, что App подставляет в <component :is>:
 *  1. компонент уже на странице — возвращается САМ компонент (без обёртки,
 *     ровно как до деления: один проход монтирования, один расчёт раскладки).
 *     Так бывает при первой загрузке (PHP подключил группу заранее
 *     <script defer>, см. VplVueTools::vueGetPageExtensions), при повторном
 *     заходе в группу в той же SPA-сессии и пока компонент ещё живёт в ядре;
 *  2. иначе — обёртка defineAsyncComponent, которая тянет расширение через
 *     BX.Runtime.loadExtension (один запрос main.loadext на все группы одного
 *     тика; файлы из BX.setJSList/тегов документа повторно не качаются),
 *     показывает заглушку с удержанием оверлея каталога, при отказе сети
 *     делает два повтора и затем выводит текст с кнопкой перезагрузки.
 *
 * Выбор запоминается на ключ блока до смены struct (App сбрасывает через
 * resetLazyChoices в watch struct): иначе следующий рендер App подменил бы
 * обёртку уже загруженного блока на «голый» компонент и перемонтировал его.
 *
 * Порядок поиска загруженного: пространство имён группы (BX.Vpl.Lk …), затем
 * ядро BX.Vpl (переходный период, пока компонент не вынесен).
 */
import { defineAsyncComponent } from 'ui.vue3';
import extensionsConfig from '../../../extensions.json';
import { holdCatalogPreloader, releaseCatalogPreloader } from '../../services/preloader/preloader';

const CORE_NAMESPACE = 'BX.Vpl';

/** имя компонента → имя расширения; строится один раз из JSON */
const componentExtension = {};
Object.entries(extensionsConfig.extensions).forEach(([ext, cfg]) => {
    cfg.components.forEach((name) => {
        componentExtension[name] = ext;
    });
});

export function extensionOf(name) {
    return componentExtension[name] || null;
}

function namespaceOf(ext) {
    const cfg = extensionsConfig.extensions[ext];
    return cfg ? cfg.namespace : null;
}

function getClass(path) {
    if (!path || typeof BX === 'undefined' || typeof BX.getClass !== 'function') {
        return null;
    }
    try {
        return BX.getClass(path) || null;
    } catch (e) {
        return null;
    }
}

function findLoaded(ext, name) {
    const group = getClass(namespaceOf(ext));
    if (group && group[name]) {
        return group[name];
    }
    const core = getClass(CORE_NAMESPACE);
    if (core && core[name]) {
        return core[name];
    }
    return null;
}

const LazyLoading = {
    mounted() {
        holdCatalogPreloader();
    },
    unmounted() {
        releaseCatalogPreloader();
    },
    template: `<div class="vpl-lazy-loading" style="display:contents"></div>`
};

const LazyError = {
    props: { error: Object },
    methods: {
        reload() {
            window.location.reload();
        }
    },
    template: `
        <div class="vpl-lazy-error" style="padding:40px 0;text-align:center">
            <p>Не удалось загрузить часть страницы.</p>
            <button type="button" class="btn" @click="reload">Обновить страницу</button>
        </div>
    `
};

const wrappers = {};

function wrapper(name, ext) {
    if (wrappers[name]) {
        return wrappers[name];
    }
    wrappers[name] = defineAsyncComponent({
        loader: () => {
            const loaded = findLoaded(ext, name);
            if (loaded) {
                return Promise.resolve(loaded);
            }
            if (!ext) {
                return Promise.reject(new Error(`vpl.tas: компонент ${name} не описан в extensions.json`));
            }
            return BX.Runtime.loadExtension(ext).then(() => {
                const component = findLoaded(ext, name);
                if (!component) {
                    throw new Error(`vpl.tas: в ${ext} нет компонента ${name}`);
                }
                return component;
            });
        },
        loadingComponent: LazyLoading,
        errorComponent: LazyError,
        delay: 0,
        suspensible: false,
        onError(error, retry, fail, attempts) {
            if (attempts <= 2) {
                retry();
            } else {
                console.error(error);
                fail();
            }
        }
    });
    return wrappers[name];
}

/** ключ блока struct → выбранный компонент (обёртка или сам компонент) */
const chosen = new Map();

export function resetLazyChoices() {
    chosen.clear();
}

export function resolveComponent(name, slotKey = null) {
    if (slotKey !== null && chosen.has(slotKey)) {
        return chosen.get(slotKey);
    }
    const ext = extensionOf(name);
    const component = findLoaded(ext, name) || wrapper(name, ext);
    if (slotKey !== null) {
        chosen.set(slotKey, component);
    }
    return component;
}

/** html-блок страницы с таким путём рендерится именованным компонентом (extensions.json → htmlPages) */
export function htmlPageComponent(pathname) {
    const pages = extensionsConfig.htmlPages || {};
    return pages[String(pathname).replace(/\/+$/, '')] || null;
}

/** известен ли компонент карте расширений (для App.getComponent) */
export function isLazyComponent(name) {
    return !!componentExtension[name];
}
